import { Grid } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import UiInput from "components/base/UiInput";
import { UpdateWorksheetProductInput } from "generated/graphql";
import useGetWorksheetProduct from "hooks/worksheetProducts/useGetWorksheetProduct";
import useUpdateWorksheetProduct from "hooks/worksheetProducts/useUpdateWorksheetProduct";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import WorksheetItemEditor from "modules/proposals/worksheet/WorksheetItemEditor";
import {
  formValuesToWorksheetProductInput,
  getWorksheetProductFormValues,
} from "modules/proposals/worksheet/worksheetFormValues";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRoute } from "utils/routing";

export default function WorksheetProductUpdatePage() {
  const navigate = useNavigate();
  const { productTypeId, productId } = useParams();
  const { data: product, loading } = useGetWorksheetProduct(productId);
  const [updateWorksheetProduct, { isSuccess }] = useUpdateWorksheetProduct();
  const [internalName, setInternalName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const defaultValues = useMemo(
    () => (product ? getWorksheetProductFormValues(product) : null),
    [product]
  );

  const handleSave = async (values: ReturnType<typeof getWorksheetProductFormValues>) => {
    if (!product) return;

    setSaving(true);

    const input = formValuesToWorksheetProductInput(
      {
        ...values,
        internalName: internalName ?? values.internalName,
      },
      { includeLibraryMeta: true }
    ) as UpdateWorksheetProductInput;

    const result = await updateWorksheetProduct({
      variables: {
        input: { ...input, id: product.id } as UpdateWorksheetProductInput,
        expectedRevision: product.revision,
      },
    });

    setSaving(false);

    if (!isSuccess(result.data)) {
      return;
    }

    navigate(getRoute("product-types.view", [["productTypeId", productTypeId ?? ""]]));
  };

  const productLabel = useMemo(() => {
    if (product) {
      return product.internalName || product.name;
    }

    return loading ? "Loading..." : "Unavailable";
  }, [product, loading]);

  return (
    <DashboardPage>
      <MDBox mb={6}>
        <Grid container alignItems="center" justifyContent="space-between">
          <MDTypography variant="h4" fontWeight="medium">
            Update Product - {productLabel}
          </MDTypography>
          <MDButton
            component={Link}
            to={getRoute("product-types.view", [["productTypeId", productTypeId ?? ""]])}
            color="blue"
          >
            Back to Products
          </MDButton>
        </Grid>
      </MDBox>

      {!loading && !product && <MDAlert color="error">This Custom product is unavailable.</MDAlert>}

      {product && defaultValues && (
        <MDBox display="flex" flexDirection="column" gap="12px">
          <MDBox sx={{ maxWidth: 420 }}>
            <MDTypography variant="caption" sx={{ color: "#8392ab" }}>
              Internal name
            </MDTypography>
            <UiInput
              uiSize="default"
              value={internalName ?? product.internalName ?? ""}
              placeholder="Internal name (library display)"
              onChange={(event) => setInternalName(event.target.value)}
            />
          </MDBox>
          <WorksheetItemEditor
            key={product.id}
            defaultValues={defaultValues}
            saving={saving}
            showProductType
            onSave={handleSave}
          />
        </MDBox>
      )}
    </DashboardPage>
  );
}
